/**
 * The mirror of personReturn.js.
 *
 * That one handles profile -> page: a profile sends the reader out to a study
 * page and stamps the hop so PersonReturnPill can offer the way back. This one
 * handles the other direction, page -> profile. A reader on the Genesis
 * explorer opens "People connected to this page", or a person link inside an
 * explorer's modal, and lands on /people/:id with no way back: the page is
 * gone from router state, and any modal that was open is gone with it, because
 * modal state lives in the page component that just unmounted.
 *
 * Deliberately a separate key and a separate module rather than a shared
 * factory: the two hops can be live at once (out to a page, then back out to a
 * profile) and must not overwrite each other.
 *
 * sessionStorage (not local) on purpose -- the hop belongs to this tab's
 * reading session and should not outlive it.
 */

import { getRouteTitle } from "./pageTitle";

function getStorage() {
  if (typeof window !== "undefined" && window.sessionStorage) {
    return window.sessionStorage;
  }

  return {
    getItem() {
      return null;
    },
    setItem() {},
    removeItem() {},
  };
}

const storage = getStorage();

const PAGE_RETURN_KEY = "pageReturn";

/**
 * PageReturnPill is mounted above the routes, so its effect reads storage
 * before a page-level effect can re-stamp the hop. This event lets it pick that
 * up in the same commit instead of waiting for the next navigation.
 */
const CHANGE_EVENT = "ctb:page-return-change";

function emitChange() {
  if (typeof window === "undefined") return;

  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch (error) {
    // fail silently
  }
}

export function subscribeToPageReturn(handler) {
  if (typeof window === "undefined") return () => {};

  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/**
 * Builds the hop without storing it, so a link can put the same object in
 * router state (which survives a back/forward) and in storage (which survives a
 * refresh on the destination).
 *
 * `label` defaults to the route's own title, which siteSearchIndex already
 * holds for every static route -- one list, not two.
 *
 * `reopen` is opaque here: a page that wants a modal restored decides what it
 * needs to identify that modal, and reads its own value back on arrival.
 */
export function buildPageReturn({ route, label, reopen } = {}) {
  if (!route) return null;

  const hop = {
    route,
    label: label || getRouteTitle(route) || "the page you were reading",
  };

  if (reopen) hop.reopen = reopen;

  return hop;
}

export function savePageReturn(hop) {
  if (!hop?.route) return;

  try {
    storage.setItem(
      PAGE_RETURN_KEY,
      JSON.stringify({ ...hop, savedAt: Date.now() })
    );
  } catch (error) {
    // fail silently
  }

  emitChange();
}

export function getPageReturn() {
  try {
    const value = storage.getItem(PAGE_RETURN_KEY);
    if (!value) return null;

    const parsed = JSON.parse(value);
    return parsed?.route ? parsed : null;
  } catch (error) {
    return null;
  }
}

export function clearPageReturn() {
  // Nothing stored means nothing changed. Skipping the event matters: the pill
  // clears whenever the reader is back on the origin route, and an
  // unconditional emit would call back into it forever.
  if (!getPageReturn()) return;

  try {
    storage.removeItem(PAGE_RETURN_KEY);
  } catch (error) {
    // fail silently
  }

  emitChange();
}
